import { Box, Grid, Stack, Text } from '@stitch-fix/mode-react';
import { MODE_MIN_WIDTH } from '../../utils/values/mediaQueries';
import {
  type CloudinaryImageFields,
  type CloudinaryImageWrapperProps,
  CloudinaryImageWrapper,
} from '../CloudinaryImageWrapper';
import {
  CloudinaryPicture,
  type CloudinaryPictureProps,
} from '../CSCloudinaryAsset/Picture';
import { GetStartedButton } from '../GetStartedButton';
import { SignInPrompt } from '../SignInPrompt';
import styles from './styles.module.scss';

export type TrigallHeroProps = {
  buttonCopy: string;
  buttonHref: string;
  description: string;
  leftImage: CloudinaryImageFields;
  topRightImage: CloudinaryImageFields;
  bottomRightImage: CloudinaryImageFields;
  mobileImage: CloudinaryImageFields;
  signInUrl?: string;
  title: string;
};

type RenderImageParams = {
  image: CloudinaryImageFields;
  sources: CloudinaryImageWrapperProps['sources'];
  pictureSources: CloudinaryPictureProps['sources'];
  imageProps?: CloudinaryPictureProps['imageProps'];
};

// TODO: Delete util and render CloudinaryPicture inline once we update all page data to use the new cloudinary asset format
const renderImage = ({
  image,
  sources,
  pictureSources,
  imageProps,
}: RenderImageParams) => {
  if (image.cloudinaryAsset) {
    return (
      <CloudinaryPicture
        alt={image.alt}
        sources={pictureSources}
        imageProps={imageProps}
        maxContainerWidth={1400}
      />
    );
  }

  return (
    <CloudinaryImageWrapper
      src={image.src}
      alt={image.alt}
      lazy={false}
      sources={sources}
    />
  );
};

export const TrigallHero = ({
  buttonCopy,
  buttonHref,
  description,
  leftImage,
  topRightImage,
  bottomRightImage,
  signInUrl,
  mobileImage,
  title,
}: TrigallHeroProps) => {
  return (
    <Box maxWidth="1400px" m="0 auto">
      <Grid columns={{ sm: 1, md: 1, lg: 2, xl: [3, 6, 3] }} spacing="none">
        {/* Left column */}
        <Box width={22.75} className={styles.leftColumn}>
          {renderImage({
            image: leftImage,
            pictureSources: [
              { minWidth: 0 },
              {
                minWidth: MODE_MIN_WIDTH.lg,
                cloudinaryAsset: leftImage?.cloudinaryAsset,
                sizes: [
                  { minWidth: MODE_MIN_WIDTH.lg, fluidWidth: 50 },
                  { minWidth: MODE_MIN_WIDTH.xl, fluidWidth: 25 },
                ],
              },
            ],
            imageProps: {
              fetchPriority: 'high',
            },
            sources: {
              sm: 304,
              lg: 360,
            },
          })}
        </Box>

        {/* Middle column */}
        <Box
          p={{ sm: 0, lg: 2 }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Stack
            spacing={0.5}
            hAlign="center"
            maxWidth="375px"
            p={{ sm: 1, md: 0 }}
          >
            <Text
              as="h1"
              setting="title-xlarge"
              height="standard"
              family="medium"
              mb={1}
              align={{ sm: 'left', lg: 'center' }}
            >
              {title}
            </Text>

            <Text
              setting="body-large"
              spacingBottom
              align={{ sm: 'left', lg: 'center' }}
            >
              {description}
            </Text>

            <GetStartedButton
              copy={buttonCopy}
              href={buttonHref}
              eventLocation="hero"
            />

            <SignInPrompt signInUrl={signInUrl} />
          </Stack>
          {/* Mobile Image */}
          <Box
            className={styles.mobileImage}
            mt={2}
            width="100%"
            maxWidth="400px"
          >
            {renderImage({
              image: mobileImage,
              pictureSources: [
                {
                  minWidth: 0,
                  cloudinaryAsset: mobileImage?.cloudinaryAsset,
                  sizes: [
                    { minWidth: 0, fluidWidth: 100 },
                    { minWidth: 400, staticWidth: 400 },
                  ],
                },
                { minWidth: MODE_MIN_WIDTH.lg },
              ],
              imageProps: {
                fetchPriority: 'high',
              },
              sources: {
                sm: 400,
              },
            })}
          </Box>
        </Box>

        {/* Right column */}
        <Box className={styles.rightColumn}>
          <Stack spacing={0.5} width={22.75}>
            <Box maxWidth={22.75}>
              {renderImage({
                image: topRightImage,
                pictureSources: [
                  { minWidth: 0 },
                  {
                    minWidth: MODE_MIN_WIDTH.xl,
                    cloudinaryAsset: topRightImage?.cloudinaryAsset,
                    sizes: [{ minWidth: MODE_MIN_WIDTH.xl, fluidWidth: 25 }],
                  },
                ],
                sources: {
                  sm: 320,
                },
              })}
            </Box>

            <Box maxWidth={22.75}>
              {renderImage({
                image: bottomRightImage,
                pictureSources: [
                  { minWidth: 0 },
                  {
                    minWidth: MODE_MIN_WIDTH.xl,
                    cloudinaryAsset: bottomRightImage?.cloudinaryAsset,
                    sizes: [{ minWidth: MODE_MIN_WIDTH.xl, fluidWidth: 25 }],
                  },
                ],
                sources: {
                  sm: 320,
                },
              })}
            </Box>
          </Stack>
        </Box>
      </Grid>
    </Box>
  );
};
